import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamWorkspaceService } from '../lib/teamWorkspaceService';
import { WorkspaceCollaborator, InviteWorkspaceCollaboratorRequest } from '../types';

export const useWorkspaceCollaborators = (workspaceId: string | null) => {
  return useQuery({
    queryKey: ['workspace-collaborators', workspaceId],
    queryFn: () => teamWorkspaceService.getWorkspaceCollaborators(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 30000,
  });
};

export const useInviteWorkspaceCollaborator = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: InviteWorkspaceCollaboratorRequest) => 
      teamWorkspaceService.inviteWorkspaceCollaborator(
        request.workspaceId,
        request.userEmail,
        request.role || 'viewer'
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-collaborators', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] });
    },
  });
};

export const useAcceptWorkspaceInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (workspaceId: string) => 
      teamWorkspaceService.acceptWorkspaceInvitation(workspaceId),
    onSuccess: (_, workspaceId) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-collaborators', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
    },
  });
};

export const useRemoveWorkspaceCollaborator = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ workspaceId, collaboratorId }: { workspaceId: string; collaboratorId: string }) => 
      teamWorkspaceService.removeWorkspaceCollaborator(workspaceId, collaboratorId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-collaborators', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] });
    },
  });
};

